import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { connect } from "react-redux";
import { generatePath } from "react-router-dom";
import { resolveFinding } from "redux/actions/Findings";
import { setOwners } from "redux/actions/Findings";
import API from "lib/api/API2";
import { copyTextToClipboard } from "utils";
import { useQueryControls } from "views/Components/ServerQueryControls";
import AlertMessage, { useAlertMessage } from "views/Components/AlertMessage";
import FindingAdvancedFilters from "views/Components/Finding/AdvancedFilters";
import FindingMoreItems from "views/Components/Finding/MoreItems";
import FindingPresets from "views/Components/Finding/Presets";
import FindingQuickFilters from "views/Components/Finding/QuickFilters";
import FindingsResolutionDialog from "views/Components/Finding/FindingsResolutionDialog";
import {
  getColumns,
  DEFAULT_COLUMN_FIELDS,
  DEFAULT_CONTROLS,
  RESOLUTION_OPTIONS,
} from "views/Pages/Finding/constants";
import ServerButton from "views/Components/ServerButton";
import ServerSearch from "views/Components/ServerSearch";
import ServerTable, {
  hasAggregationFields,
  compileParams,
  sanitize,
} from "views/Components/ServerTable";
import ServerToolbar from "views/Components/ServerToolbar";
import { TimeControls } from "views/Components/TimespanPicker";

import { Root } from "./styles";

const FindingsListView2 = ({
  assignCurrentUserAsResponder,
  currentUser,
  dispatchResolveFinding,
  gotoFinding,
  gotoScheduledReports,
  license,
  orgId,
}) => {
  const [activeFields, setActiveFields] = useState(DEFAULT_COLUMN_FIELDS);
  const [alertMessageProps, displayMessage] = useAlertMessage();
  const findingSelectedIds = useRef([]);
  const [hasIntegrations, setHasIntegrations] = useState(true);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [isResolutionNotesDialogOpen, setIsResolutionNotesDialogOpen] =
    useState(false);
  const [queryControls, queryControlInterface] = useQueryControls(
    "finding",
    DEFAULT_CONTROLS
  );
  const [refreshTable, setRefreshTable] = useState(0);
  const [resolution, setResolution] = useState(10);

  const refreshFindings = () => {
    setRefreshTable(refreshTable + 1);
  };

  const reset = () => {
    setActiveFields(DEFAULT_COLUMN_FIELDS);
    setIsAdvancedFiltersOpen(false);
    setIsResolutionNotesDialogOpen(false);
    queryControlInterface.restore(); // Changing controls will update table
  };

  const checkHasIntegrations = async () => {
    const params = [{ field: "outputFields", value: ["id"] }];
    try {
      const api = new API();
      const response = await api.get("integration", compileParams(params), []);
      const hasIntegs = !!response?.meta?.totalItems;
      if (hasIntegs !== hasIntegrations) {
        setHasIntegrations(hasIntegs);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    reset();
    license?.isTagged("limited-to-free") && checkHasIntegrations();
  }, [orgId]);

  const handleFindingResolutionMenu = (option) => {
    setResolution(parseInt(option.value));
    setIsResolutionNotesDialogOpen(true);
  };

  const handleFindingResolutions = async (resolutionNotes) => {
    if (!findingSelectedIds.current) {
      displayMessage({ severity: "error", message: "Nothing selected" });
      return;
    }

    // Build data with user notes
    const data = {
      status: 40,
      resolution: resolution,
      resolutionNotes: resolutionNotes,
    };

    // Assigning owners expects an array of objects with a value
    // that matches a user id.
    const ownerData = [{ value: currentUser.id }];

    // Assign user as responder and resolve selected findings.
    Promise.all(
      // This spawns a promise/request for every selected finding that
      // will all be run in the background.
      findingSelectedIds.current.map(async (findingId) => {
        await assignCurrentUserAsResponder({
          ownerValues: ownerData,
          findingId: findingId,
        });
        await dispatchResolveFinding({
          data,
          findingId,
        });
      })
    )
      .then(() => {
        // TODO Dispatch seems to return immediately so it doesn't actually
        // wait properly before running this `then`.
        setIsResolutionNotesDialogOpen(false);
        refreshFindings();
      })
      .catch((err) => {
        displayMessage({
          severity: "error",
          message: `Failed to resolve one or more findings: ${err}`,
          noAutoHide: true,
        });
      });
  };

  const handleViewDetails = (clickedRow) => {
    const id = clickedRow.params.row.id;
    gotoFinding({ id, orgId });
  };

  const handleMenuCopy = (clickedRow) => {
    const anchorEl = clickedRow?.event?.target.closest(".MuiDataGrid-cell");
    copyTextToClipboard(anchorEl.innerText);
  };

  const handleOpenInNewTab = (clickedRow) => {
    const id = clickedRow.params.row.id;
    const path = generatePath("/:orgId/reporting/findings/:id", { id, orgId });
    window.open(path, "_blank");
  };

  const handleSelectionChange = useCallback((selectedIds) => {
    findingSelectedIds.current = selectedIds;
  }, []);

  // Avoid refetching data grid when parent rerenders
  const tableParams = useMemo(() => {
    const outputFields = activeFields.filter((f) => f !== "ownersExpanded");
    const hasCount = Object.values(queryControls).some(
      (p) => p.field === "count" || p.operator === "count"
    );
    if (hasCount && !outputFields.includes("grouped_count")) {
      outputFields.push("grouped_count");
    }
    const params = [
      {
        field: "expandedOwners",
        value: true,
      },
      {
        field: "outputFields",
        value: outputFields,
      },
    ];
    if (!license?.isTagged("limited-to-free") || hasIntegrations) {
      params.push({
        field: "name",
        operator: "!contains",
        value: "[SAMPLE]",
      });
    }

    return params.concat(Object.values(queryControls));
  }, [activeFields, hasIntegrations, orgId, queryControls, refreshTable]);

  return (
    <Root>
      <ServerToolbar>
        <FindingPresets
          controlInterface={queryControlInterface}
          displayMessage={displayMessage}
          orgId={orgId}
          setActiveFields={setActiveFields}
        />
        <TimeControls
          controls={queryControls}
          delta="-30:d"
          interface={queryControlInterface}
        />
        <div>
          <ServerSearch
            controlInterface={queryControlInterface}
            controls={queryControls}
          />
          <ServerButton
            datacy="advancedFiltersButton"
            onClick={() => setIsAdvancedFiltersOpen(!isAdvancedFiltersOpen)}
            tooltip="Advanced Filters"
            variant={isAdvancedFiltersOpen ? "outlined" : "text"}
          >
            Advanced
          </ServerButton>
        </div>
        <FindingMoreItems
          activeFields={activeFields}
          controls={queryControls}
          displayMessage={displayMessage}
          gotoScheduledReports={gotoScheduledReports}
          orgId={orgId}
          reset={reset}
        />
      </ServerToolbar>
      {isAdvancedFiltersOpen && (
        <FindingAdvancedFilters
          activeFields={activeFields}
          setActiveFields={setActiveFields}
          controls={queryControls}
          controlInterface={queryControlInterface}
          onApplyFilters={() => setIsAdvancedFiltersOpen(false)}
          onClearFilters={reset}
        />
      )}
      <FindingQuickFilters
        controlInterface={queryControlInterface}
        controls={queryControls}
        orgId={orgId}
      />
      <ServerTable
        checkboxSelectionActions={[
          {
            onClick: handleFindingResolutionMenu,
            label: "Resolve selected as",
            type: "dropdown",
            options: RESOLUTION_OPTIONS,
          },
        ]}
        columns={getColumns(activeFields)}
        contextMenuItems={
          hasAggregationFields(tableParams)
            ? [{ onClick: handleMenuCopy, text: "Copy to Clipboard" }]
            : [
                {
                  onClick: handleViewDetails,
                  text: "View Finding Details",
                  datacy: "findingsContextMenuItemFindingsDetail",
                },
                { onClick: handleMenuCopy, text: "Copy to Clipboard" },
                {
                  onClick: handleOpenInNewTab,
                  text: "Open in New Tab",
                },
              ]
        }
        endpoint={"/finding"}
        formatData={sanitize}
        onCheckboxSelectionChange={handleSelectionChange}
        params={tableParams}
        refresh={refreshTable}
        sortable={true}
        defaultSort={[
          { field: "status", sort: "asc" },
          { field: "created", sort: "desc" },
        ]}
        uniqueName="FindingListTable"
      />
      <AlertMessage {...alertMessageProps} />
      <FindingsResolutionDialog
        open={isResolutionNotesDialogOpen}
        onClose={() => setIsResolutionNotesDialogOpen(false)}
        handleResolveFindings={handleFindingResolutions}
      />
    </Root>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    orgId: state.page.payload.orgId,
    license: state.license,
    currentUser: state.session.settings.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    gotoFinding: ({ id, orgId }) => {
      dispatch({
        type: "PAGE",
        payload: {
          orgId,
          toplevel: "reporting",
          secondlevel: "findings",
          id1: id,
        },
      });
    },
    gotoScheduledReports: ({ orgId }) => {
      dispatch({
        type: "PAGE",
        payload: {
          orgId: orgId,
          toplevel: "reporting",
          secondlevel: "scheduledreports",
        },
      });
    },
    dispatchResolveFinding: ({ findingId, data }) => {
      dispatch(resolveFinding({ findingId, data }));
    },
    assignCurrentUserAsResponder: ({ ownerValues, findingId }) => {
      dispatch(
        setOwners({
          findingId: findingId,
          ownerType: "responder",
          owners: (ownerValues || []).map((i) => i.value),
        })
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FindingsListView2);
